/**
 * The AI producer's direction (`DirectorResponse.showPlan`) → what the player renders. The model
 * decides headings, hero lines, emphasis and the visual-cue sequence; this module only maps that
 * direction onto the stage vocabulary and times it against the narration's word timings
 * (audio is the master clock — a cue never appears before the words that carry it).
 */
import type { DirectorResponse, ShowChapter, ShowPlan, VisualCue } from '@clutch/core';
import type { Word } from './show';

export type Graphic = 'player-hero' | 'score-slam' | 'shot-marker' | 'momentum' | 'official-embed' | 'call-receipt' | 'reaction' | 'title-card' | 'beat';

/** Cue type → stage graphic. `silence` is a deliberate beat: no graphic at all. */
export function cueGraphic(cue: Pick<VisualCue, 'type'>): Graphic {
  switch (cue.type) {
    case 'player': return 'player-hero';
    case 'score-change': return 'score-slam';
    case 'court-event': return 'shot-marker';
    case 'momentum': return 'momentum';
    case 'official-embed': return 'official-embed';
    case 'fan-call': return 'call-receipt';
    case 'reaction': return 'reaction';
    case 'title': return 'title-card';
    default: return 'beat';
  }
}

export type Treatment = {
  /** CSS modifier on the player. */
  tone: 'restrained' | 'tension' | 'cinematic' | 'slow';
  /** Score changes slam only when the producer asked for excitement. */
  slam: boolean;
  letterbox: boolean;
  /** Relative transition tempo (1 = default). */
  tempo: number;
};

/** Emphasis → treatment: quiet restrained, build tension, excited slam/cinematic, reflective slow. */
export function emphasisTreatment(emphasis: ShowChapter['emphasis'] | undefined): Treatment {
  switch (emphasis) {
    case 'quiet': return { tone: 'restrained', slam: false, letterbox: false, tempo: 0.8 };
    case 'build': return { tone: 'tension', slam: false, letterbox: false, tempo: 1 };
    case 'excited': return { tone: 'cinematic', slam: true, letterbox: true, tempo: 1.4 };
    case 'reflective': return { tone: 'slow', slam: false, letterbox: false, tempo: 0.6 };
    default: return { tone: 'tension', slam: false, letterbox: false, tempo: 1 };
  }
}

/** A plan is usable only when it directs the segments the server actually returned. */
export function usablePlan(d: Pick<DirectorResponse, 'showPlan' | 'segments'>): ShowPlan | undefined {
  const p = d.showPlan;
  if (!p || !Array.isArray(p.chapters) || !p.chapters.length) return undefined;
  const ids = new Set(d.segments.map((s) => s.segmentId));
  return p.chapters.some((c) => ids.has(c.segmentId)) ? p : undefined;
}

export function chapterFor(plan: ShowPlan | undefined, segmentId: string | undefined): ShowChapter | undefined {
  return plan && segmentId ? plan.chapters.find((c) => c.segmentId === segmentId) : undefined;
}

const norm = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/**
 * Word index at which each cue starts, in the producer's order. A cue whose subject is named in
 * the narration (its player's surname, or a score from its event) lands on that word; otherwise
 * the cues share the spoken chapter evenly. Starts increase, so cues stay in order.
 */
export function cueStarts(cues: readonly VisualCue[], words: readonly Pick<Word, 'text'>[], hints: (cue: VisualCue) => string[] = () => []): number[] {
  const n = words.length;
  if (!cues.length) return [];
  if (!n) return cues.map(() => 0);
  const tokens = words.map((w) => norm(w.text));
  const starts: number[] = [];
  let floor = 0;
  cues.forEach((cue, i) => {
    const even = Math.floor((i * n) / cues.length);
    const wanted = hints(cue).map(norm).filter((h) => h.length > 1);
    // Each cue gets its own word when the chapter has enough of them (strictly increasing starts).
    const min = i ? Math.min(n - 1, floor + 1) : 0;
    let at = -1;
    if (wanted.length) for (let k = min; k < n; k++) if (wanted.includes(tokens[k]!)) { at = k; break; }
    const start = Math.max(min, at >= 0 ? at : even);
    starts.push(Math.min(n - 1, start));
    floor = starts[i]!;
  });
  return starts;
}

/** The cue on screen when `spoken` words have started (from the audio clock); -1 before the first. */
export function activeCue(starts: readonly number[], spokenIndex: number): number {
  let active = -1;
  for (let i = 0; i < starts.length; i++) if (starts[i]! <= spokenIndex) active = i;
  return active;
}

/** Without word timings, the same mapping over the chapter's audio progress (0–1). */
export function activeCueByProgress(count: number, fraction: number): number {
  if (!count) return -1;
  return Math.min(count - 1, Math.max(0, Math.floor(Math.max(0, Math.min(0.999, fraction)) * count)));
}
